<template>
  <div
    :id="pageId"
    :class="pageId"
    class="page-content"
  >
    <div class="steps-container">
      <el-steps
        :active="activeStep"
        align-center
        finish-status="success"
        :aria-label="`Registration progress: step ${activeStep + 1} of ${TOTAL_STEPS}`"
      >
        <el-step
          v-for="(step, index) in stepsConfig"
          :key="index"
          :title="step.title"
          :description="step.description"
        />
      </el-steps>
    </div>

    <component
      :is="currentStepComponent"
      @valid="handleValidityChange"
      @goto-next-step="handleStepNavigation"
    />

    <div class="form-submit-button-group mb-2">
      <p-button
        v-if="showBackButton"
        id="btnBack"
        class="mr-2"
        round
        type="warning"
        @click="goToPreviousStep"
      >
        {{ backButtonText }}
      </p-button>

      <p-button
        v-if="showNextButton"
        id="btnNext"
        round
        type="purple"
        :disabled="isNextButtonDisabled"
        @click="handleStepNavigation"
      >
        {{ nextButtonText }}
      </p-button>

      <p-button
        v-if="isSignupLaterAvailable"
        id="btnSignupLater"
        type="danger"
        round
        @click="onSignupLater"
      >
        Sign Up Later
      </p-button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { GroceryRegistrationSteps } from '@/models/grocery';
import type { Component } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useCustomer } from '@/use/customer';
import { lead, leadCaller } from '../../LeadRegistration/viewModel';

// Import components
import ServiceCheck from '@/components/Main/Operator/Grocery/Registration/ServiceCheck.vue';
import Estimate from '@/components/Main/Operator/Grocery/Registration/Estimate.vue';
import CustomerInformation from '@/components/Main/Operator/Grocery/Registration/CustomerInformation.vue';
import Instacart from '@/components/Main/Operator/Grocery/Registration/Instacart.vue';
import Membership from '@/components/Main/Operator/Grocery/Registration/Membership.vue';
import BillingInformation from '@/components/Main/Operator/Grocery/Registration/BillingInformation.vue';
import Confirmation from '@/components/Main/Operator/Grocery/Registration/Confirmation.vue';

// Constants
const pageId = 'grocery-registration-page';
const TOTAL_STEPS = 7;

const stepsConfig = [
  { title: 'Step 1', description: 'Service check' },
  { title: 'Step 2', description: 'Estimate' },
  { title: 'Step 3', description: 'Customer information' },
  { title: 'Step 4', description: 'Save cart from Instacart' },
  { title: 'Step 5', description: 'Choose membership' },
  { title: 'Step 6', description: 'Billing Information' },
  { title: 'Step 7', description: 'Confirmation' },
];

const route = useRoute();
const router = useRouter();

const { prefillCustomerInfoFromLeadCallerData } = useCustomer();

const isFromLeadRegistration = route.query.from === 'lead';

// Component mapping
const componentMap: Record<GroceryRegistrationSteps, Component> = {
  [GroceryRegistrationSteps.ServiceCheck]: ServiceCheck,
  [GroceryRegistrationSteps.Estimate]: Estimate,
  [GroceryRegistrationSteps.CustomerInformation]: CustomerInformation,
  [GroceryRegistrationSteps.SaveCartFromInstacart]: Instacart,
  [GroceryRegistrationSteps.ChooseMembership]: Membership,
  [GroceryRegistrationSteps.BillingInformation]: BillingInformation,
  [GroceryRegistrationSteps.Confirmation]: Confirmation,
};

// Configuration
const SKIPPABLE_STEPS = new Set([GroceryRegistrationSteps.Estimate, GroceryRegistrationSteps.SaveCartFromInstacart]);

// Reactive state
const activeStep = ref<GroceryRegistrationSteps>(GroceryRegistrationSteps.ServiceCheck);
const codeName = ref<string>('');
const stepValidity = ref<Record<GroceryRegistrationSteps, boolean>>(
  Object.values(GroceryRegistrationSteps)
    .filter((v) => typeof v === 'number')
    .reduce(
      (acc, step) => {
        acc[step as GroceryRegistrationSteps] = SKIPPABLE_STEPS.has(step as GroceryRegistrationSteps);
        return acc;
      },
      {} as Record<GroceryRegistrationSteps, boolean>,
    ),
);

// Computed properties
const currentStepComponent = computed<Component>(() => componentMap[activeStep.value]);
const showBackButton = computed<boolean>(() => activeStep.value !== GroceryRegistrationSteps.ServiceCheck);
const showNextButton = computed<boolean>(() => activeStep.value !== GroceryRegistrationSteps.Confirmation);
const isNextButtonDisabled = computed<boolean>(() => !SKIPPABLE_STEPS.has(activeStep.value) && !stepValidity.value[activeStep.value]);

const nextButtonText = computed<string>(() =>
  activeStep.value === GroceryRegistrationSteps.Confirmation ? (codeName.value ? 'Complete' : 'Complete Registration') : 'Go To Next Step',
);

const backButtonText = computed<string>(() => `Back to Step ${activeStep.value + 1}`);
const isSignupLaterAvailable = computed(() => isFromLeadRegistration && activeStep.value === GroceryRegistrationSteps.ChooseMembership);

// Event handlers
const handleValidityChange = (isValid: boolean) => {
  stepValidity.value[activeStep.value] = isValid;
};

const goToPreviousStep = (): void => {
  if (activeStep.value > GroceryRegistrationSteps.ServiceCheck) {
    activeStep.value--;
  }
};

const onSignupLater = () => {
  router.push({
    name: 'FacebookRegistration',
    query: {
      from: 'registration',
    },
  });
};

const handleStepNavigation = async (): Promise<void> => {
  if (activeStep.value < GroceryRegistrationSteps.Confirmation) {
    activeStep.value++;
  }
};

const prefillData = () => {
  if (route.query.from !== 'lead') {
    return;
  }

  prefillCustomerInfoFromLeadCallerData(lead.value, leadCaller.value);

  activeStep.value = GroceryRegistrationSteps.CustomerInformation;
};

onMounted(prefillData);
</script>

<style lang="scss" scoped>
.steps-container {
  margin-bottom: 2rem;
}

.form-submit-button-group {
  display: flex;
  justify-content: center;
  margin-top: 2rem;
  gap: 0.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;

    button {
      margin: 0.5rem 0;
      width: 100%;
    }
  }
}
</style>
